import { posDb } from '@/db/pos-database';
import type { PendingSyncEntry } from '@/db/pos-database';
import { encryptForStorage, decryptManyFromStorage } from '@/db/encrypted-access';
import { hasActiveKey } from '@/db/crypto';
import { ENC_COLUMN } from '@/db/encryption-config';
import { syncService, type SyncTransaction } from '@/services/sync.service';
// G2.19: local-quote cleanup after successful QUOTATION sync. This forms a
// circular import (offline-quote.service → enqueue → sync-queue.service →
// offlineQuoteService) but JS resolves it correctly because the usage is
// inside a function body, not at module load. Both modules are fully
// initialized by the time processQueue runs.
import { offlineQuoteService } from '@/services/offline-quote.service';

// ─── Constants ──────────────────────────────────────────────────────────────

const MAX_BATCH_SIZE = 100;
const MAX_RETRIES = 10;
const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS = 60_000;
/**
 * T17: how often a DEAD row gets one more try while the browser stays online.
 * The browser's `online` event never fires when it is the API that was down.
 */
const DEAD_PROBE_INTERVAL_MS = 2 * 60_000;
/** last_error of a row this till cannot read: encrypted under another device key. */
const UNREADABLE_ERROR =
  'Saved under an earlier sign-in or device key — it syncs when that sign-in returns on this till';
/** T19: a synced bill stays in the cashier's offline-bills list this long (owner decision). */
const SYNCED_RETENTION_MS = 24 * 60 * 60 * 1000;
/** Statuses the cashier must look at (status-bar badge, T19). */
const ATTENTION_STATUSES = ['FAILED', 'DEAD', 'CONFLICT'] as const;

// ─── State ──────────────────────────────────────────────────────────────────

let isProcessing = false;
/** A trigger that arrived mid-run; the run goes again when it ends. */
let rerunRequested = false;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let interruptedRequeue: Promise<void> | null = null;

// ─── Listeners ──────────────────────────────────────────────────────────────

/** `attentionCount` (T19): failed, dead and conflicting rows — the status-bar badge. */
type QueueChangeListener = (pendingCount: number, conflictCount: number, attentionCount: number) => void;
const listeners = new Set<QueueChangeListener>();

async function notifyListeners() {
  const count = await getPendingCount();
  const conflicts = await getConflictCount();
  const attention = await getAttentionCount();
  for (const fn of listeners) fn(count, conflicts, attention);
}

export function onQueueChange(fn: QueueChangeListener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/**
 * A push run that actually sent something: `active` true when its first batch
 * goes out, false when it ends (`clean` = nothing left pending or in conflict).
 * Lets the sync status page show the automatic reconnect sync, not only the
 * manual "Push" button.
 */
type SyncActivityListener = (active: boolean, clean: boolean) => void;
const activityListeners = new Set<SyncActivityListener>();

export function onSyncActivity(fn: SyncActivityListener): () => void {
  activityListeners.add(fn);
  return () => {
    activityListeners.delete(fn);
  };
}

/**
 * T19: a bill the server refused or that ran out of tries — the cashier gets
 * a toast with the reason. Not fired for a network failure with retries left:
 * an outage would otherwise toast on every backoff.
 */
export interface BillProblem {
  client_id: string;
  /** The till's number on the receipt (OFL…), when the row could be read. */
  offline_number: string | null;
  status: 'FAILED' | 'DEAD' | 'CONFLICT';
  /** Raw reason (server message or code); the UI words it. */
  reason: string;
}
type BillProblemListener = (problem: BillProblem) => void;
const problemListeners = new Set<BillProblemListener>();

export function onBillProblem(fn: BillProblemListener): () => void {
  problemListeners.add(fn);
  return () => {
    problemListeners.delete(fn);
  };
}

function reportBillProblem(entry: PendingSyncEntry, status: BillProblem['status'], reason: string): void {
  if (entry.entity_type !== 'BILL') return;
  const number = (entry.payload as Record<string, unknown> | undefined)?.bill_number;
  const problem: BillProblem = {
    client_id: entry.client_id,
    offline_number: typeof number === 'string' ? number : null,
    status,
    reason,
  };
  for (const fn of problemListeners) fn(problem);
}

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Add a transaction to the offline sync queue.
 */
export async function enqueue(entry: Omit<PendingSyncEntry, 'id'>): Promise<void> {
  const encrypted = await encryptForStorage(entry, 'pending_sync');
  await posDb.pending_sync.add(encrypted as PendingSyncEntry);
  await notifyListeners();

  // Try to process immediately if online
  if (navigator.onLine && !isProcessing) {
    processQueue();
  }
}

/**
 * A Manager resolved the conflict this till's transaction raised: its local
 * row (parked in CONFLICT) is settled and must stop showing as stuck
 * (sync plan Phase 5). Returns how many rows were removed.
 */
export async function clearResolvedConflict(
  clientId: string,
  outcome: { resolution?: string; entityId?: string | null } = {},
): Promise<number> {
  // T19: a bill a manager accepted as sold (the only outcome a refused bill
  // has) is synced — it stays in the cashier's offline-bills list 24 h like any
  // other. The event carries the bill id, not its number.
  let settled = 0;
  if (outcome.resolution === 'CLIENT_WINS') {
    settled = await posDb.pending_sync
      .where('client_id')
      .equals(clientId)
      .and((entry) => entry.status === 'CONFLICT' && entry.entity_type === 'BILL')
      .modify({
        status: 'SYNCED',
        server_id: outcome.entityId ?? null,
        server_bill_number: null,
        synced_at: new Date().toISOString(),
        last_error: null,
      });
  }
  const removed = await posDb.pending_sync
    .where('client_id')
    .equals(clientId)
    .and((entry) => entry.status === 'CONFLICT')
    .delete();
  if (settled + removed > 0) await notifyListeners();
  return settled + removed;
}

/**
 * Get count of pending (unsent) transactions: PENDING, FAILED and DEAD (T17 —
 * a row that ran out of retries is still unsent). CONFLICT rows are counted by
 * `getConflictCount`; PROCESSING rows are on their way.
 */
export async function getPendingCount(): Promise<number> {
  return posDb.pending_sync.where('status').anyOf(['PENDING', 'FAILED', 'DEAD']).count();
}

/**
 * Every row still on the device, whatever its status — all of them are lost if
 * IndexedDB is wiped (T17: logout and key rotation wait for this to be 0).
 */
export async function getUnsyncedCount(): Promise<number> {
  return posDb.pending_sync
    .where('status')
    .anyOf(['PENDING', 'PROCESSING', 'FAILED', 'DEAD', 'CONFLICT'])
    .count();
}

/**
 * Get count of transactions stuck in conflict (awaiting manual resolution).
 */
export async function getConflictCount(): Promise<number> {
  return posDb.pending_sync.where('status').equals('CONFLICT').count();
}

/** T19: rows the cashier must look at — failed, out of tries, or refused (needs review). */
export async function getAttentionCount(): Promise<number> {
  return posDb.pending_sync.where('status').anyOf([...ATTENTION_STATUSES]).count();
}

/** T19: drop synced bills older than 24 h from the offline-bills list. */
export async function purgeExpiredSyncedBills(now: number = Date.now()): Promise<number> {
  const cutoff = new Date(now - SYNCED_RETENTION_MS).toISOString();
  return posDb.pending_sync
    .where('status')
    .equals('SYNCED')
    .and((entry) => !entry.synced_at || entry.synced_at < cutoff)
    .delete();
}

/**
 * T19: every bill this till holds — unsynced, refused, or synced in the last
 * 24 h — decrypted, newest first. A row written under a device key that is not
 * loaded comes back sealed (no payload fields); the list says so.
 */
export async function listOfflineBillEntries(): Promise<PendingSyncEntry[]> {
  await purgeExpiredSyncedBills();
  const rows = await posDb.pending_sync.where('entity_type').equals('BILL').toArray();
  const decrypted = await decryptManyFromStorage(rows);
  return decrypted.sort((a, b) => (a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : 0));
}

/**
 * T19: the cashier's Retry on one bill (or 'Retry all' with no id). The row
 * goes back in line with a fresh set of tries and the till pushes — offline it
 * simply waits, PENDING, for the connection. A refused bill (CONFLICT) is sent
 * again too: the server keeps one open review per bill, so a retry that is
 * refused again adds nothing for the manager.
 */
export async function retryEntries(clientId?: string): Promise<number> {
  await requeueInterrupted();
  const retried = await posDb.pending_sync
    .where('status')
    .anyOf([...ATTENTION_STATUSES])
    .and((entry) => clientId === undefined || entry.client_id === clientId)
    .modify({ status: 'PENDING', retry_count: 0 });
  if (retried > 0) await notifyListeners();
  await processQueue();
  return retried;
}

/**
 * Once per page load, before this page marks anything PROCESSING: a row still
 * PROCESSING belongs to a push whose answer an earlier page never saw (reload or
 * close mid-sync). Nothing else picks those up, so put them back in line —
 * re-pushing is safe because the server dedupes on client_id (DUPLICATE).
 *
 * T17: an older till left rows FAILED with every retry spent, which nothing
 * fetched or listed. They are DEAD — visible, counted and revived like any other.
 */
function requeueInterrupted(): Promise<void> {
  interruptedRequeue ??= posDb.pending_sync
    .where('status')
    .equals('PROCESSING')
    .modify({ status: 'PENDING' })
    .then(() =>
      posDb.pending_sync
        .where('status')
        .equals('FAILED')
        .and((entry) => entry.retry_count >= MAX_RETRIES)
        .modify({ status: 'DEAD' }),
    )
    .then(() => undefined);
  return interruptedRequeue;
}

/** Exponential backoff for the next push after a failure. */
function backoffDelay(retryCount: number): number {
  return Math.min(BASE_DELAY_MS * Math.pow(2, retryCount), MAX_DELAY_MS);
}

/**
 * Rows still encrypted after decryption were written under a device key that
 * is not in memory. Pushed, they carry no payload and the server refuses the
 * whole batch, so they never go out. Before the key is installed (page load)
 * they are left alone; with another key installed they are parked DEAD, still
 * listed and counted, until the sign-in that wrote them returns.
 */
async function readableEntries(
  entries: PendingSyncEntry[],
  unreadable: Set<number>,
): Promise<PendingSyncEntry[]> {
  const readable: PendingSyncEntry[] = [];
  for (const entry of entries) {
    if (!(ENC_COLUMN in entry)) {
      readable.push(entry);
      continue;
    }
    unreadable.add(entry.id!);
    if (hasActiveKey()) {
      await posDb.pending_sync.update(entry.id!, { status: 'DEAD' as const, last_error: UNREADABLE_ERROR });
    }
  }
  return readable;
}

/**
 * T17: put DEAD rows back in line, then push. `attempts` is how many tries they
 * get before turning DEAD again — the full budget on a real signal (reconnect,
 * 'Sync now', app start, logout), one try for the slow probe.
 */
export async function retryDeadEntries(attempts: number = MAX_RETRIES): Promise<void> {
  await requeueInterrupted();
  const revived = await posDb.pending_sync
    .where('status')
    .equals('DEAD')
    .modify({ status: 'PENDING', retry_count: MAX_RETRIES - attempts });
  if (revived > 0) await notifyListeners();
  await processQueue();
}

/** The slow probe: one more try for DEAD rows, only when there are some. */
async function probeDeadEntries(): Promise<void> {
  if (!navigator.onLine || isProcessing) return;
  const dead = await posDb.pending_sync.where('status').equals('DEAD').count();
  if (dead > 0) await retryDeadEntries(1);
}

/**
 * Process the offline queue: batch-push pending transactions to the server.
 */
export async function processQueue(): Promise<void> {
  if (!navigator.onLine) return;
  if (isProcessing) {
    // e.g. the device key was installed while a run skipped rows it could not read
    rerunRequested = true;
    return;
  }
  isProcessing = true;
  rerunRequested = false;
  let pushed = false;
  const unreadable = new Set<number>();

  try {
    await requeueInterrupted();
    await purgeExpiredSyncedBills();
    // eslint-disable-next-line no-constant-condition
    while (true) {
      // Only fetch entries that haven't exceeded max retries
      const rawBatch = await posDb.pending_sync
        .where('status')
        .anyOf(['PENDING', 'FAILED'])
        .and((entry) => entry.retry_count < MAX_RETRIES && !unreadable.has(entry.id!))
        .limit(MAX_BATCH_SIZE)
        .toArray();
      if (rawBatch.length === 0) break;
      const batch = await readableEntries(await decryptManyFromStorage(rawBatch), unreadable);
      if (batch.length === 0) continue;
      if (!pushed) {
        pushed = true;
        for (const fn of activityListeners) fn(true, false);
      }

      // Mark batch as PROCESSING
      const ids = batch.map((e) => e.id!);
      await posDb.pending_sync.where('id').anyOf(ids).modify({ status: 'PROCESSING' });

      const transactions: SyncTransaction[] = batch.map((entry) => ({
        client_id: entry.client_id,
        entity_type: entry.entity_type as SyncTransaction['entity_type'],
        operation: entry.operation as SyncTransaction['operation'],
        payload: entry.payload,
        device_id: entry.device_id,
      }));

      // Highest retry count among bills the server answered with ERROR.
      let refusedRetry = 0;
      try {
        const result = await syncService.push(transactions);

        // Process individual results
        for (const r of result.results) {
          const entry = batch.find((e) => e.client_id === r.client_id);
          if (!entry?.id) continue;

          if (r.status === 'SYNCED' || r.status === 'DUPLICATE') {
            if (entry.entity_type === 'BILL') {
              // T19: keep the bill 24 h with its final number (Shopify: the
              // receipt's OFL number becomes the INV number on sync).
              await posDb.pending_sync.update(entry.id, {
                status: 'SYNCED' as const,
                server_id: r.server_id ?? null,
                server_bill_number: r.bill_number ?? null,
                synced_at: new Date().toISOString(),
                last_error: null,
              });
            } else {
              // Successfully synced or already existed — remove from queue
              await posDb.pending_sync.delete(entry.id);
            }

            // G2.19: Entity-specific local cleanup. For QUOTATION, we need to
            // (a) mark the local_quotes row as synced with the server-assigned
            //     server_id, and then
            // (b) remove the local row entirely so QuotationListPage stops
            //     showing the offline copy alongside the server copy.
            //
            // markQuoteSynced is called first as a safety net: if the
            // subsequent delete fails (e.g. IndexedDB quota transient error),
            // the row is still labeled synced so the next sync pass can clean
            // it up without re-pushing. Both operations are best-effort —
            // we log but don't surface failures to the cashier because the
            // server state is already correct.
            if (entry.entity_type === 'QUOTATION' && r.server_id) {
              try {
                await offlineQuoteService.markQuoteSynced(r.client_id, r.server_id);
                await offlineQuoteService.removeLocalQuote(r.client_id);
              } catch (cleanupErr) {
                console.warn(
                  `[sync-queue] Local quote cleanup failed for ${r.client_id}:`,
                  cleanupErr instanceof Error ? cleanupErr.message : cleanupErr,
                );
              }
            }
          } else if (r.status === 'CONFLICT') {
            // Conflict detected — mark as CONFLICT, awaiting manual resolution.
            // T19: keep the server's reason so the cashier sees "Needs review: <reason>".
            const reason =
              r.error_message || r.conflict_reason || 'Sync conflict — awaiting Manager/Owner resolution';
            await posDb.pending_sync.update(entry.id, {
              status: 'CONFLICT' as const,
              last_error: reason,
              retry_count: entry.retry_count + 1,
            });
            reportBillProblem(entry, 'CONFLICT', reason);
          } else {
            // ERROR: the server could not take this one. Keep its reason; out
            // of retries it is DEAD (listed and counted), never a hidden FAILED.
            const newRetryCount = entry.retry_count + 1;
            const reason = r.error_message || `Server answered ${r.status}`;
            refusedRetry = Math.max(refusedRetry, newRetryCount);
            await posDb.pending_sync.update(
              entry.id,
              newRetryCount >= MAX_RETRIES
                ? {
                    status: 'DEAD' as const,
                    last_error: `Max retries exceeded. Last error: ${reason}`,
                    retry_count: newRetryCount,
                  }
                : { status: 'FAILED' as const, last_error: reason, retry_count: newRetryCount },
            );
            // Told once on the first refusal and once when it gives up — not on
            // every backoff retry in between.
            if (newRetryCount >= MAX_RETRIES) reportBillProblem(entry, 'DEAD', reason);
            else if (entry.retry_count === 0) reportBillProblem(entry, 'FAILED', reason);
          }
        }
      } catch (err) {
        // Network error or server error — revert batch to FAILED with backoff
        for (const entry of batch) {
          if (!entry.id) continue;
          const newRetryCount = entry.retry_count + 1;

          if (newRetryCount >= MAX_RETRIES) {
            // Exceeded max retries — mark permanently dead
            const reason = err instanceof Error ? err.message : 'Unknown error';
            await posDb.pending_sync.update(entry.id, {
              status: 'DEAD' as const,
              last_error: `Max retries exceeded. Last error: ${reason}`,
              retry_count: newRetryCount,
            });
            reportBillProblem(entry, 'DEAD', reason);
          } else {
            await posDb.pending_sync.update(entry.id, {
              status: 'FAILED' as const,
              last_error: err instanceof Error ? err.message : 'Unknown error',
              retry_count: newRetryCount,
            });
          }
        }

        // Schedule retry with exponential backoff (use NEW retry count)
        const maxRetry = Math.max(...batch.map((e) => e.retry_count + 1));
        scheduleRetry(backoffDelay(maxRetry));
        break; // Stop processing on network error
      }

      await notifyListeners();
      if (refusedRetry > 0) {
        // T17: back off like a network failure. Fetching the row again now
        // spent all its retries in one pass.
        scheduleRetry(backoffDelay(refusedRetry));
        break;
      }
    }
  } finally {
    isProcessing = false;
    await notifyListeners();
    if (pushed) {
      const clean = (await getPendingCount()) === 0 && (await getConflictCount()) === 0;
      for (const fn of activityListeners) fn(false, clean);
    }
    // A backoff already scheduled covers the missed trigger.
    if (rerunRequested && !retryTimer) void processQueue();
  }
}

/**
 * Schedule a retry of the queue after a delay.
 */
function scheduleRetry(delayMs: number): void {
  if (retryTimer) clearTimeout(retryTimer);
  retryTimer = setTimeout(() => {
    retryTimer = null;
    if (navigator.onLine) processQueue();
  }, delayMs);
}

/**
 * Setup auto-sync on reconnect. Call once at app init.
 */
export function initAutoSync(): () => void {
  // T17: reconnecting is the signal a DEAD row waits for.
  const handleOnline = () => {
    void retryDeadEntries();
  };

  window.addEventListener('online', handleOnline);
  const deadProbe = setInterval(() => {
    void probeDeadEntries();
  }, DEAD_PROBE_INTERVAL_MS);

  // Try processing any leftover queue on init
  if (navigator.onLine) {
    void retryDeadEntries();
  }

  return () => {
    window.removeEventListener('online', handleOnline);
    clearInterval(deadProbe);
    if (retryTimer) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
  };
}
